import { Alert, Button, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from './Dialogs/FormDialog';
import { ConfirmDialog } from './Dialogs/ConfirmDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { Project } from '../../core/types/data-models';

interface NewProjectDialogProps {
  open: boolean;
  onClose: () => void;
  /** When provided, the dialog operates in edit mode. */
  project?: Project;
  onCreated?: (project: Project) => void;
  /** Called after the project (and its documents) have been deleted. */
  onDeleted?: (project: Project) => void;
}

export function NewProjectDialog({ open, onClose, project, onCreated, onDeleted }: NewProjectDialogProps) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(project);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(project?.name ?? '');
    setDescription(project?.description ?? '');
    setError(null);
  }, [open, project]);

  const create = useMutation({
    mutationFn: (payload: Partial<Project>) => resources.projects.create(payload),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['my-projects'] });
      onCreated?.(created);
      onClose();
    },
  });

  const update = useMutation({
    mutationFn: (payload: Partial<Project>) => resources.projects.update(project!.id, payload),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['my-projects'] });
      onCreated?.(updated);
      onClose();
    },
  });

  const remove = useMutation({
    mutationFn: () => resources.projects.remove(project!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['my-projects'] });
      setConfirmDeleteOpen(false);
      onDeleted?.(project!);
      onClose();
    },
    onError: (err) => {
      setConfirmDeleteOpen(false);
      setError(extractErrorMessage(err));
    },
  });

  const isPending = create.isPending || update.isPending;

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    try {
      const payload = { name: name.trim(), description: description.trim() || undefined };
      if (isEdit) {
        await update.mutateAsync(payload);
      } else {
        await create.mutateAsync(payload);
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <>
      <FormDialog
        open={open}
        title={isEdit ? 'Edit project' : 'New project'}
        submitLabel={isEdit ? 'Save' : 'Create'}
        loading={isPending}
        onClose={handleClose}
        onSubmit={handleSubmit}
        startAction={
          isEdit ? (
            <Button
              color="warning"
              variant="outlined"
              disabled={isPending}
              onClick={() => setConfirmDeleteOpen(true)}
            >
              Delete
            </Button>
          ) : undefined
        }
      >
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Project name"
          required
          autoFocus
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label="Description"
          fullWidth
          multiline
          minRows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          helperText="Optional — describe what this project is for."
        />
      </FormDialog>

      {isEdit && (
        <ConfirmDialog
          open={confirmDeleteOpen}
          title="Delete project?"
          message={`Are you sure you want to delete "${project!.name}"? This will permanently delete the project and all of its documents. This action cannot be undone.`}
          confirmLabel="Delete"
          destructive
          loading={remove.isPending}
          onConfirm={() => remove.mutate()}
          onClose={() => setConfirmDeleteOpen(false)}
        />
      )}
    </>
  );
}
